<template>
  <div class="device-calibration">
    <component
      v-for="field in calibrationFields"
      :key="field.dataCy"
      :is="field.componentName"
      customClass="small-subtitle"
      v-bind="field"
    />
  </div>
</template>

<script>
import popoverTimestamp from '@/components/devicePopover/content/popoverTimestamp.vue'
import popoverNumber from '@/components/devicePopover/content/popoverNumber.vue'
import popoverText from '@/components/devicePopover/content/popoverText.vue'
import {
  getCalibrationDateFromState,
  getFlowCalibrationFactorFromState,
  getPhotometricCalFactorFromState,
  getSizeCalFactorFromState,
  getCalibrationNameFromState,
} from '@/helpers/studies/calibrationHelper'
import { getLatestStudyTelemetry } from '@/helpers/studies/telemetryHelper'
import { getLatestStudyState } from '@/helpers/studies/stateHelper'
import { activeLastDay } from '@/services/device-status'

export default {
  name: 'DeviceCalibration',
  components: {
    'popover-timestamp': popoverTimestamp,
    'popover-number': popoverNumber,
    'popover-text': popoverText,
  },
  props: {
    deviceId: String,
    deviceStudy: Object,
    showStudyContent: Boolean,
  },
  computed: {
    latestStudyTelemetry() {
      return getLatestStudyTelemetry(this.deviceStudy)
    },
    lastTelemetryTimestamp() {
      return this.showStudyContent
        ? this.latestStudyTelemetry?.timestamp
        : this.$store.getters['devices/getLastContactByDeviceId'](this.deviceId)
    },
    latestStudyState() {
      return getLatestStudyState(this.deviceStudy)
    },
    isTelemetryActive() {
      return activeLastDay(this.lastTelemetryTimestamp)
    },
    calibrationDate() {
      return this.showStudyContent
        ? getCalibrationDateFromState(this.latestStudyState)
        : this.$store.getters['devices/getCalibrationDateByDeviceId'](
            this.deviceId
          )
    },
    flowCalibrationFactor() {
      return this.showStudyContent
        ? getFlowCalibrationFactorFromState(this.latestStudyState)
        : this.$store.getters['devices/getFlowCalibrationFactorByDeviceId'](
            this.deviceId
          )
    },
    photometricCalFactor() {
      return this.showStudyContent
        ? getPhotometricCalFactorFromState(this.latestStudyState)
        : this.$store.getters['devices/getPhotometricCalFactorByDeviceId'](
            this.deviceId
          )
    },
    sizeCalFactor() {
      return this.showStudyContent
        ? getSizeCalFactorFromState(this.latestStudyState)
        : this.$store.getters['devices/getSizeCalFactorByDeviceId'](
            this.deviceId
          )
    },
    calibrationName() {
      return this.showStudyContent
        ? getCalibrationNameFromState(this.latestStudyState)
        : this.$store.getters['devices/getCalibrationNameByDeviceId'](
            this.deviceId
          )
    },
    calibrationFields() {
      return [
        {
          componentName: 'popover-text',
          value: this.getFormattedValue(this.calibrationName),
          label: this.$t('widget.currentReadings.calName'),
          invalidText: this.$t('map.popover.valueBlank'),
          dataCy: 'calName',
        },
        {
          componentName: 'popover-number',
          value: this.getFormattedValue(this.photometricCalFactor),
          label: this.$t('widget.currentReadings.photometricCalFactor'),
          dataCy: 'photometricCalFactor',
        },
        {
          componentName: 'popover-timestamp',
          value: this.getFormattedValue(this.calibrationDate),
          label: this.$t('widget.currentReadings.calibrationDate'),
          invalidText: this.$t('map.popover.valueBlank'),
          showTimeZoneDate: true,
          dataCy: 'calibrationDate',
        },
        {
          componentName: 'popover-number',
          value: this.getFormattedValue(this.sizeCalFactor),
          label: this.$t('widget.currentReadings.sizeCalFactor'),
          dataCy: 'sizeCalFactor',
        },
        {
          componentName: 'popover-number',
          value: this.getFormattedValue(this.flowCalibrationFactor),
          label: this.$t('widget.currentReadings.flowCalibrationFactor'),
          dataCy: 'flowCalibrationFactor',
        },
      ]
    },
  },
  methods: {
    getFormattedValue(value) {
      return this.isTelemetryActive ? value : null
    },
  },
}
</script>

<style lang="scss" scoped>
@import '@/assets/global-variables.scss';

.device-calibration {
  display: flex;
  justify-content: space-between;
  flex-wrap: wrap;

  .small-subtitle {
    padding: 0;
    line-height: 1.4;
    min-width: 35%;
  }

  .small-subtitle:nth-child(odd) {
    padding-left: 12px;
  }

  .small-subtitle:nth-child(even) {
    text-align: end;
    padding-right: 12px;
  }

  .small-subtitle:last-child {
    width: 100%;
    padding: 0;
    background-color: rgba($great-light-shade, 0.6);
    text-align: center;
  }
}
</style>
